import { path } from 'd3-path'
import pointer from './helpers/pointer.js'
import Symbol2d from '../2d.js'
import tipLock from './helpers/tip-lock.js'

export default class QuadBoxArrow extends Symbol2d {

  static mixins = [tipLock]

  handles = [{
    // first handle controls the arrow height/width
    defaults: [.85, .4],
    constraints: (dimensions, handles) => {
      const yMax = .49
      const y = Math.min(yMax, handles[0].coords[1])

      // Make sure the handle doesn't allow the vertical arrows to extend beyond the width
      const yc1 = (dimensions.center[1] - dimensions.width * .25) / dimensions.height
      const yc2 = .25 // the y value can't go above 25% b/c the arrow will be too tall and extend the height
      const yConstraint = [Math.max(yc1, yc2), yMax]

      // Determine x constraints
      const tip = Math.max(dimensions.center[1] - dimensions.height * y, 0)
      let maxArrowHeight = dimensions.center[1] - tip * 2

      const xc1 = (dimensions.width - maxArrowHeight) / dimensions.width
      const xc2 = (dimensions.center[0] + tip * 2) / dimensions.width
      const xc3 = handles[1].coords[0] // The right boundary of the box
      const xConstraint = [Math.max(xc1, xc2, xc3), 1]

      return [xConstraint, yConstraint]
    },
    cursor: 'all-scroll'
  }, {
    // second handle controls the box width
    defaults: [.7, 0],
    constraints: (dimensions, handles) => {
      const pointerHeight = (dimensions.width - handles[0].coords[0] * dimensions.width)

      const maxBoxHeight = dimensions.height - pointerHeight * 2
      const maxWidth = handles[0].coords[0] // The max x value cannot exceed handles[0].coords[0]
      const maxHeight = ((maxBoxHeight / (2 * (dimensions.height / dimensions.width))) + dimensions.center[0]) / dimensions.width
      const maxX = Math.min(maxWidth, maxHeight)

      const pointerWidth = (dimensions.center[1] - dimensions.height * handles[0].coords[1]) * 2
      const minBoxHeight = pointerWidth
      const minHeight = ((minBoxHeight / (2 * (dimensions.height / dimensions.width))) + dimensions.center[0]) / dimensions.width
      const minWidth = (dimensions.center[0] + pointerWidth / 2) / dimensions.width
      const minX = Math.max(.5, minWidth, minHeight)

      return [
        [minX, maxX], null
      ]
    },
    cursor: 'ew-resize'
  }]

  getHandles (dimensions) {
    let handles = this._getDefaultHandles()

    // The 2nd handle that controls the box width `y` value will always be
    // equal to the first value `y`, which controls the arrow width.
    handles[1].coords[1] = handles[0].coords[1]
    return [
      handles[0],
      handles[1]
    ].map(handle => this.enforceConstraints(handle, dimensions, handles))
  }

  path (dimensions, handles) {
    const _path = path()
    const _pointer = pointer(_path, dimensions, handles[0])
    const halfBox = (handles[1][0] * dimensions.width - dimensions.center[0])

    const boxHeight = ((handles[1][0] * dimensions.width - dimensions.center[0]) * 2) * (dimensions.height / dimensions.width)
    const halfBase = _pointer.width / 2
    const start = [dimensions.center[0] - halfBase, dimensions.center[1] - boxHeight / 2]

    // First arrow is north
    _path.moveTo(...start)
    _pointer(start, 'north')

    _path.lineTo(dimensions.center[0] + halfBox, dimensions.center[1] - boxHeight / 2)
    _pointer([dimensions.center[0] + halfBox, dimensions.center[1] - _pointer.width / 2], 'east')

    _path.lineTo(dimensions.center[0] + halfBox, dimensions.center[1] + boxHeight / 2)
    _pointer([dimensions.center[0] + halfBase, dimensions.center[1] + boxHeight / 2], 'south')

    _path.lineTo(dimensions.center[0] - halfBox, dimensions.center[1] + boxHeight / 2)
    _pointer([dimensions.center[0] - halfBox, dimensions.center[1] + halfBase], 'west')

    _path.lineTo(dimensions.center[0] - halfBox, dimensions.center[1] - boxHeight / 2)
    _path.lineTo(dimensions.center[0] - halfBase, dimensions.center[1] - boxHeight / 2)
    _path.closePath()

    return _path.toString()
  }

  textContainer (dimensions, handles) {
    // Use the inner box
    // TODO this is copy pasted from above. Cleanup
    const width = (handles[1][0] * dimensions.width - dimensions.center[0]) * 2
    const height = ((handles[1][0] * dimensions.width - dimensions.center[0]) * 2) * (dimensions.height / dimensions.width)

    return {
      center: dimensions.center,
      width,
      height
    }
  }
}
